import { useEffect, useState } from "react";

import { SearchConditionItem, SettingUser } from "@/types/setting";

import { GetSearchConditionList, SetSearchCondition } from "@/services/Setting";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import CommonImg from "@/components/Common/Img";
import CommonButton from "@/components/Common/Button";
import CommonDialogKeywordGroupItem from "@/components/DialogKeywordGroupItem";

import CogWheelIcon from "@/assets/images/cogwheel-icon.png";
import BlackPlusIcon from "@/assets/images/black-plus-icon.png";

type Props = {
  onRefresh: () => void;
};

export default function DialogKeywordGroup({ onRefresh }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [settingList, setSettingList] = useState<SearchConditionItem[]>([]);

  const handleSave = async () => {
    try {
      const params = `request=[${settingList.map(
        (setting) =>
          `UserSearchSettingsRequest(` +
          `settings=None, ` +
          `search_id=${setting.search_id === "None" ? `None` : `"${setting.search_id}"`}, ` +
          `search_name="${setting.search_name}", ` +
          `pinned=${setting.pinned ? "True" : "False"}, ` +
          `user_ids=${JSON.stringify(setting.users.map((user) => `${user.user_id}`))}` +
          `)`
      )}]\n`;
      const result = await SetSearchCondition(params);
      if (result.data.success) {
        setSettingList(result.data.pods[0].content.items);
        onRefresh();
        setOpen(false);
      }
    } catch (e) {
    } finally {
    }
  };

  const onLoad = async () => {
    try {
      const result = await GetSearchConditionList();
      setSettingList(result.data.pods[0].content.items);
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    if (open) {
      onLoad();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <CommonImg
          src={CogWheelIcon}
          alt={"Cog Wheel"}
          className={"size-[28px]"}
        />
      </DialogTrigger>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>검색 조건 관리</DialogTitle>
        </DialogHeader>
        <DialogDescription />
        <div className="flex flex-col gap-[16px]">
          <div className="h-[430px] overflow-auto flex flex-col gap-[8px]">
            {settingList.map((setting, settingIndex) => (
              <CommonDialogKeywordGroupItem
                key={settingIndex}
                item={setting}
                itemIndex={settingIndex}
                itemListLength={settingList.length}
                onChangeTitle={(value: string) => {
                  setSettingList((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === settingIndex
                        ? { ...item, search_name: value }
                        : item
                    )
                  );
                }}
                onDelete={() => {
                  setSettingList((prev) =>
                    prev.filter((item, itemIndex) => itemIndex !== settingIndex)
                  );
                }}
                onPin={() => {
                  setSettingList((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === settingIndex
                        ? { ...item, pinned: true }
                        : { ...item, pinned: false }
                    )
                  );
                }}
                onAddUser={(user) => {
                  setSettingList((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === settingIndex
                        ? {
                            ...item,
                            users: item.users.concat(user),
                          }
                        : item
                    )
                  );
                }}
                onRemoveUser={(user) => {
                  setSettingList((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === settingIndex
                        ? {
                            ...item,
                            users: item.users.filter(
                              (id) => id.user_id !== user.user_id
                            ),
                          }
                        : item
                    )
                  );
                }}
              />
            ))}
          </div>
          <div className="flex justify-center">
            <CommonImg
              src={BlackPlusIcon}
              alt="Plus"
              className="size-[24px] cursor-pointer"
              onClick={() => {
                setSettingList((prev) => [
                  ...prev,
                  {
                    search_id: "None",
                    search_name: "",
                    pinned: prev.length <= 0,
                    users: [] as SettingUser[],
                  } as SearchConditionItem,
                ]);
              }}
            />
          </div>
          <div className="flex justify-center">
            <CommonButton
              label="설정 완료"
              className="w-[150px] bg-dark-red text-sm font-semibold"
              onClick={handleSave}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
